import React from "react";
import type {
  TokenDemandForecastPoint,
  TokenDemandMonitorPoint,
  TokenDemandSliceResult,
  TokenDemandSourceHealth,
} from "../../models/tokenDemand";
import { MetricChart } from "../ChartBoard";

export interface TokenDemandPanelProps {
  mode: "monitor" | "forecast";
  result: TokenDemandSliceResult;
}

function value(value: number | null, digits = 1) {
  return value === null ? "—" : value.toLocaleString("zh-CN", { maximumFractionDigits: digits });
}

function metric(valueToFormat: number) {
  return valueToFormat.toLocaleString("zh-CN", { maximumFractionDigits: 2 });
}

function statusLabel(status: TokenDemandSourceHealth["status"]) {
  if (status === "current") return "已更新";
  if (status === "degraded") return "有缺口";
  if (status === "stale") return "已滞后";
  return "不可用";
}

function intervalLabel(point: TokenDemandForecastPoint) {
  return point.interval.level === 0.9 ? "90% OOS conformal" : "未校准 fallback";
}

function TrendChart({
  actual,
  forecast = [],
}: {
  actual: TokenDemandMonitorPoint[];
  forecast?: TokenDemandForecastPoint[];
}) {
  const recentActual = actual.slice(-24);
  if (recentActual.length < 2) return <div className="empty-state compact"><strong>暂无历史趋势</strong></div>;

  return (
    <div className="chart-shell">
      <MetricChart
        detailed
        chart={{
          label: "月度行业需求指数历史与预测趋势",
          unit: "点",
          historyLabel: "行业需求代理实际值",
          history: recentActual.map((point) => ({ period: point.month, value: point.value })),
          baseline: forecast.map((point) => ({
            period: point.month,
            value: point.predicted,
            low: point.low,
            high: point.high,
            detail: `${point.horizon}M 预测 · ${intervalLabel(point)}`,
          })),
          intervalLabel: forecast.length ? intervalLabel(forecast[0]) : undefined,
        }}
      />
    </div>
  );
}

function SourceDetails({ result }: { result: TokenDemandSliceResult }) {
  const rows = [result.monitor.sourceHealth.npm, result.monitor.sourceHealth.wikimedia, result.monitor.sourceHealth.openrouter];
  return (
    <details className="sheet limitation-sheet">
      <summary>方法、口径与来源</summary>
      <div className="method-grid">
        <div><span>目标定义</span><p>{result.methodology.targetDefinition}</p></div>
        <div><span>模型</span><p>{result.methodology.formula}</p></div>
        <div><span>回测</span><p>{result.methodology.validation}</p></div>
        <div><span>区间</span><p>{result.methodology.interval}</p></div>
      </div>
      <div className="table-shell">
        <table className="research-table source-table">
          <caption>月度需求模型的数据水位与口径</caption>
          <thead><tr><th>来源</th><th>角色</th><th>水位 / watermark</th><th>覆盖</th><th>缺月</th><th>状态</th></tr></thead>
          <tbody>{rows.map((health) => {
            const source = result.sources.find((candidate) => candidate.id === health.sourceId);
            return <tr key={health.sourceId}><td>{health.sourceId}</td><td>{source?.role ?? "复合代理"}</td><td>{health.watermark?.slice(0, 10) ?? "—"}</td><td>{health.coverage.firstMonth ?? "—"} — {health.coverage.lastMonth ?? "—"}</td><td>{health.missingMonths.length}</td><td>{statusLabel(health.status)}</td></tr>;
          })}</tbody>
        </table>
      </div>
      <ul>{result.methodology.caveats.map((caveat) => <li key={caveat}>{caveat}</li>)}</ul>
    </details>
  );
}

function MonitorView({ result }: { result: TokenDemandSliceResult }) {
  const proxy = result.monitor.industryDemandProxy;
  const platform = result.monitor.platformTokenActual;
  const proxyHealth = result.monitor.sourceHealth.demandProxy;
  const routerHealth = result.monitor.sourceHealth.openrouter;
  return (
    <div className="workspace-view token-demand-panel monitor-view">
      <section className="sheet monitor-section" aria-labelledby="token-monitor-heading">
        <div className="sheet-heading">
          <div><div className="section-kicker">REAL-TIME DATA MONITOR</div><h2 id="token-monitor-heading">Token 与 AI 需求监控</h2><p>平台实际量和行业需求代理分开呈现，不推算全球 Token、DAU、ARR 或模型厂收入。</p></div>
          <span className="coverage-stamp">完整月 {result.monitor.latestCompleteMonth}</span>
        </div>
        <div className="monitor-grid">
          <article className="monitor-card">
            <div className="monitor-card-head"><span>行业需求代理</span><small>{proxy.latestMonth ?? "—"}</small></div>
            <div className="monitor-values"><span><small>最新指数</small><strong>{value(proxy.latestValue)}</strong></span><i>·</i><span><small>数据水位</small><b>{proxyHealth.watermark?.slice(0, 10) ?? "—"}</b></span></div>
            <div className="monitor-card-foot"><b>{statusLabel(proxyHealth.status)}</b><span>npm × Wikimedia · {proxyHealth.missingMonths.length} 个缺月</span></div>
          </article>
          <article className="monitor-card">
            <div className="monitor-card-head"><span>OpenRouter 平台 Token 实际量</span><small>{platform.latestMonth ?? "未接入"}</small></div>
            <div className="monitor-values"><span><small>平台 Tokens</small><strong>{platform.latestValue === null ? "—" : `${value(platform.latestValue, 2)}T`}</strong></span><i>·</i><span><small>数据水位</small><b>{routerHealth.watermark?.slice(0, 10) ?? "—"}</b></span></div>
            <div className="monitor-card-foot"><b>{statusLabel(routerHealth.status)}</b><span>单平台口径 · 非全球总量</span></div>
          </article>
        </div>
      </section>
      <section className="sheet chart-sheet" aria-labelledby="token-history-heading">
        <div className="sheet-heading compact"><div><h3 id="token-history-heading">行业需求代理历史趋势</h3><p>{proxy.warning}</p></div><span className="row-count">{proxy.points.length} MONTHS</span></div>
        <TrendChart actual={proxy.points} />
      </section>
      <SourceDetails result={result} />
    </div>
  );
}

function ForecastView({ result }: { result: TokenDemandSliceResult }) {
  const horizons = [result.forecast.horizons.month1, result.forecast.horizons.month3, result.forecast.horizons.month12];
  const primary = result.backtest.primary;
  const baselineRows = [result.backtest.baselines.lag1, result.backtest.baselines.seasonalLag12, result.backtest.baselines.drift];
  return (
    <div className="workspace-view token-demand-panel forecast-view">
      <section className="sheet chart-sheet" aria-labelledby="token-forecast-heading">
        <div className="sheet-heading">
          <div><div className="section-kicker">MONTHLY FORECAST</div><h2 id="token-forecast-heading">月度 AI 需求指数预测</h2><p>主模型预测行业需求代理；OpenRouter 只保留为单平台 challenger。</p></div>
          <div className="forecast-summary"><span>12M 终点</span><strong>{value(result.forecast.horizons.month12.predicted)}</strong><small>{intervalLabel(result.forecast.horizons.month12)} {value(result.forecast.horizons.month12.low)} — {value(result.forecast.horizons.month12.high)}</small><b>{result.forecast.scenarioImpact.affectsCurrentChart ? "当前假设直接影响本图" : "当前图为基准情景"}</b></div>
        </div>
        <TrendChart actual={result.monitor.industryDemandProxy.points} forecast={result.forecast.points} />
      </section>
      <section className="diagnostic-strip" aria-label="Token 需求预测摘要">
        {horizons.map((point) => <div key={point.horizon}><span>{point.horizon}M 预测</span><strong>{value(point.predicted)}</strong><small>{point.month} · {intervalLabel(point)}</small></div>)}
        <div><span>Rolling-origin MAPE</span><strong>{primary.metrics.full.mape.toFixed(1)}%</strong><small>{primary.metrics.full.observations} 个同窗样本外月份</small></div>
      </section>
      <section className="sheet error-table-sheet">
        <div className="sheet-heading compact"><div><h3>1M / 3M / 12M 路径与区间</h3><p>{result.forecast.scenarioImpact.explanation}</p></div><span className="row-count">12 MONTHS</span></div>
        <div className="table-shell">
          <table className="research-table error-table">
            <caption>月度需求指数未来路径、基准与区间来源</caption>
            <thead><tr><th>月份</th><th>Horizon</th><th>基准</th><th>当前预测</th><th>区间</th><th>方法</th></tr></thead>
            <tbody>{result.forecast.points.map((point, index) => <tr key={point.month}><td>{point.month}</td><td>{point.horizon}M</td><td>{value(result.forecast.baselinePoints[index].predicted)}</td><td>{value(point.predicted)}</td><td>{value(point.low)} — {value(point.high)}</td><td>{intervalLabel(point)}</td></tr>)}</tbody>
          </table>
        </div>
      </section>
      <details className="sheet accuracy-sheet">
        <summary>回测准确性与基线比较</summary>
        <div className="table-shell">
          <table className="research-table">
            <caption>相同 rolling-origin 预测月份的全窗与近期误差</caption>
            <thead><tr><th>模型</th><th>全窗 RMSE</th><th>全窗 MAPE</th><th>近期 6M MAPE</th><th>近期 12M MAPE</th><th>相对主模型</th></tr></thead>
            <tbody>
              <tr><td>{primary.label}</td><td>{metric(primary.metrics.full.rmse)}</td><td>{primary.metrics.full.mape.toFixed(1)}%</td><td>{primary.metrics.recent6.mape.toFixed(1)}%</td><td>{primary.metrics.recent12.mape.toFixed(1)}%</td><td>主模型</td></tr>
              {baselineRows.map((model) => {
                const key = model.id === "lag-1" ? "lag1" : model.id === "seasonal-lag-12" ? "seasonalLag12" : "drift";
                const skill = result.backtest.skillVsBaselinesPct[key];
                return <tr key={model.id}><td>{model.label}</td><td>{metric(model.metrics.full.rmse)}</td><td>{model.metrics.full.mape.toFixed(1)}%</td><td>{model.metrics.recent6.mape.toFixed(1)}%</td><td>{model.metrics.recent12.mape.toFixed(1)}%</td><td>{skill === null ? "—" : `主模型 ${skill >= 0 ? "+" : ""}${skill.toFixed(1)}% skill`}</td></tr>;
              })}
            </tbody>
          </table>
        </div>
      </details>
      <SourceDetails result={result} />
    </div>
  );
}

/** Pure view: the parent owns the product's two top-level Monitor/Forecast tabs. */
export function TokenDemandPanel({ mode, result }: TokenDemandPanelProps) {
  return mode === "monitor" ? <MonitorView result={result} /> : <ForecastView result={result} />;
}
